import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye, ExternalLink, History, RefreshCcw } from 'lucide-react';
import { SetupRecord } from '../../types/deployment';
import {
  SETUP_STATUS_COLORS,
  SETUP_STATUS_LABELS,
  formatDateTime,
  formatSetupActionLabel,
  getProgressSummaryLines,
} from './setupExplorerUtils';

export type SetupRunHistoryState = {
  items: SetupRecord[];
  total: number;
  loading: boolean;
  loaded: boolean;
  error: string;
};

interface SetupHistoryPanelProps {
  historyState?: SetupRunHistoryState;
  onLoadMore: () => void;
  onOpenRunDetails: (setup: SetupRecord) => void;
  onRefreshHistory: () => void;
  onRefreshSetup: (setupId: string) => void;
  saving?: boolean;
}

const renderGitLabLink = (setup: SetupRecord) => {
  if (!setup.gitlab?.pipelineWebUrl && !setup.gitlab?.jobWebUrl) {
    return null;
  }

  const href = setup.gitlab.jobWebUrl || setup.gitlab.pipelineWebUrl || '';
  const label = setup.gitlab.jobName
    || (setup.gitlab.pipelineId ? `Pipeline #${setup.gitlab.pipelineId}` : 'Open GitLab');

  return (
    <Typography
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="caption"
      color="primary"
      sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {label}
      <ExternalLink size={12} />
    </Typography>
  );
};

export const SetupHistoryPanel: React.FC<SetupHistoryPanelProps> = ({
  historyState,
  onLoadMore,
  onOpenRunDetails,
  onRefreshHistory,
  onRefreshSetup,
  saving = false,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 1.5,
        ml: { md: 4 },
        bgcolor: 'rgba(255,255,255,0.02)',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <History size={16} />
            <Typography variant="subtitle2">Historical Setup Runs</Typography>
            {historyState?.loaded ? (
              <Chip size="small" variant="outlined" label={`${historyState.items.length}/${historyState.total}`} />
            ) : null}
          </Stack>
          <Button
            size="small"
            variant="text"
            startIcon={historyState?.loading ? <CircularProgress size={14} /> : <RefreshCcw size={14} />}
            disabled={historyState?.loading || saving}
            onClick={onRefreshHistory}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        {historyState?.error ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {historyState.error}
          </Alert>
        ) : null}

        {historyState?.loading && !historyState.loaded ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : null}

        {historyState?.loaded && historyState.items.length === 0 && !historyState.loading ? (
          <Typography variant="body2" color="text.secondary">
            No older setup runs were found for this target yet.
          </Typography>
        ) : null}

        <Stack spacing={1.25}>
          {(historyState?.items || []).map((setup) => (
            <Card key={setup._id} variant="outlined" sx={{ px: 1.5, py: 1.25 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
                alignItems={{ md: 'center' }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={600}>
                      {formatSetupActionLabel(setup.actionType)}
                    </Typography>
                    {setup.setupMode ? (
                      <Chip size="small" variant="outlined" label={setup.setupMode} />
                    ) : null}
                    <Chip
                      size="small"
                      color={SETUP_STATUS_COLORS[setup.status] || 'default'}
                      label={SETUP_STATUS_LABELS[setup.status] || setup.status}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {setup.currentStep || setup.failedStep || 'No active step'} • Started {formatDateTime(setup.startedAt ?? setup.createdAt)}
                  </Typography>
                  {getProgressSummaryLines(setup).slice(0, 2).map((line) => (
                    <Typography key={`${setup._id}-${line}`} variant="caption" color="text.secondary">
                      {line}
                    </Typography>
                  ))}
                  {renderGitLabLink(setup)}
                </Stack>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="View full details">
                    <span>
                      <IconButton size="small" onClick={() => onOpenRunDetails(setup)}>
                        <Eye size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {['requested', 'running'].includes(setup.status) ? (
                    <Tooltip title="Refresh status from GitLab">
                      <span>
                        <IconButton size="small" disabled={saving} onClick={() => onRefreshSetup(setup._id)}>
                          <RefreshCcw size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>

        {historyState && historyState.items.length < historyState.total ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadMore}
              disabled={historyState.loading || saving}
            >
              Load More History
            </Button>
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};
